import { create } from 'zustand'
import { DEFAULT_STYLE_ID } from '@/lib/constants'
import type { StyleId, AspectRatio, ModelId } from '@/lib/constants'
import type { ReferenceImage, ApplyableSettings } from '@/types/generation'
import type { QueueItem, QueueItemParams } from '@/types/queue'
import { QUEUE_MAX_SIZE } from '@/types/queue'
import {
  parseTemplateParameters,
  resolveTemplate,
  extractDefaultValues,
} from '@/lib/template-parameters'

interface GeneratedImage {
  id: string
  url: string
  thumbnailUrl?: string
  liked: boolean
}

interface SelectedTemplate {
  id: string
  name: string
  description: string | null
  thumbnailUrl: string | null
  nameI18n?: Record<string, string> | null
  descriptionI18n?: Record<string, string> | null
}

interface TemplateParameterState {
  /** 원본 파라미터화된 템플릿 프롬프트 */
  templateRawPrompt: string | null
  /** 현재 파라미터 값 맵 */
  templateParameters: Record<string, string> | null
}

interface GeneratorState extends TemplateParameterState {
  prompt: string
  style: StyleId
  model: ModelId
  aspectRatio: AspectRatio
  count: number
  referenceImages: ReferenceImage[]
  /** @deprecated Use queue state instead. Kept for backward compatibility. */
  isGenerating: boolean
  results: GeneratedImage[]
  lastPrompt: string
  selectedImage: string | null
  /** 현재 적용된 템플릿 (템플릿 모드일 때만 값이 있음) */
  selectedTemplate: SelectedTemplate | null
  /** 이미지 생성 큐 */
  queue: QueueItem[]
  /** 사용량 한도 초과 모달 표시 여부 */
  usageLimitModalOpen: boolean
  /** SSO 토큰 만료 모달 표시 여부 */
  ssoTokenExpiredModalOpen: boolean
  /** 안전 정책 제한 모달 표시 여부 */
  safetyErrorModalOpen: boolean
  /** 안전 정책에 의해 플래그된 카테고리 목록 */
  safetyFlaggedCategories: string[]
  /** 이미지 생성을 한 번이라도 했는지 (세션 내 힌트용, persist 불필요) */
  hasGeneratedOnce: boolean
  /** 프롬프트 입력창 포커스 트리거 (값이 바뀔 때마다 포커스) */
  promptFocusTrigger: number

  /** 프롬프트 직접 설정 (파라미터 모드 해제됨) */
  setPrompt: (prompt: string) => void
  /** 파라미터 값 업데이트 후 프롬프트 재계산 */
  setTemplateParameter: (key: string, value: string) => void
  setStyle: (style: StyleId) => void
  setModel: (model: ModelId) => void
  setAspectRatio: (ratio: AspectRatio) => void
  setCount: (count: number) => void
  setReferenceImages: (images: ReferenceImage[]) => void
  addReferenceImage: (image: ReferenceImage) => void
  removeReferenceImagesByGenerationId: (generationId: string) => void
  /** @deprecated Managed by queue processor now. */
  setIsGenerating: (isGenerating: boolean) => void
  setResults: (results: GeneratedImage[]) => void
  setLastPrompt: (prompt: string) => void
  setSelectedImage: (url: string | null) => void
  toggleLike: (imageId: string) => void
  reset: () => void
  /** 템플릿 설정 (템플릿 모드 진입) */
  setSelectedTemplate: (template: SelectedTemplate | null) => void
  /** 템플릿 모드 해제 (스타일 모드로 복귀) */
  clearTemplate: () => void
  /**
   * 외부 소스(히스토리, 템플릿, 커뮤니티)에서 설정을 적용
   * 기존 results는 유지하고 폼 값만 변경
   */
  applySettings: (settings: ApplyableSettings) => void

  // Queue actions
  /** 새 생성 요청을 큐에 추가. 큐 ID 반환 */
  enqueueGeneration: (params: QueueItemParams) => string | null
  /** pending 상태의 큐 아이템 취소 */
  cancelQueueItem: (queueId: string) => void
  /** failed 상태의 큐 아이템 재시도 */
  retryQueueItem: (queueId: string) => void
  /** completed/failed 상태의 아이템 정리 */
  clearCompletedItems: () => void
  /** 내부용: 큐 아이템 상태 업데이트 */
  _updateQueueItem: (queueId: string, updates: Partial<QueueItem>) => void
  /** 사용량 한도 초과 모달 표시 설정 */
  setUsageLimitModalOpen: (open: boolean) => void
  /** SSO 토큰 만료 모달 표시 설정 */
  setSsoTokenExpiredModalOpen: (open: boolean) => void
  /** 안전 정책 제한 모달 표시 설정 */
  setSafetyErrorModalOpen: (open: boolean) => void
  /** 안전 정책 플래그 카테고리 설정 */
  setSafetyFlaggedCategories: (categories: string[]) => void
  /** 생성 완료 힌트 dismiss */
  dismissGeneratedOnceHint: () => void
  /** 프롬프트 입력창에 포커스 요청 */
  requestPromptFocus: () => void
}

const initialState = {
  prompt: '',
  style: DEFAULT_STYLE_ID,
  model: '' as ModelId, // 백엔드에서 isDefault: true인 모델로 초기화됨
  aspectRatio: '4:3' as AspectRatio,
  count: 2,
  referenceImages: [] as ReferenceImage[],
  isGenerating: false,
  results: [],
  lastPrompt: '',
  selectedImage: null,
  selectedTemplate: null,
  queue: [] as QueueItem[],
  // Template parameter state
  templateRawPrompt: null as string | null,
  templateParameters: null as Record<string, string> | null,
  // Usage limit modal
  usageLimitModalOpen: false,
  // SSO token expired modal
  ssoTokenExpiredModalOpen: false,
  // Safety error modal
  safetyErrorModalOpen: false,
  // Safety flagged categories
  safetyFlaggedCategories: [] as string[],
  // Generated once hint
  hasGeneratedOnce: false,
  // Prompt focus trigger
  promptFocusTrigger: 0,
}

/** UUID 생성 헬퍼 */
function generateUUID(): string {
  return crypto.randomUUID()
}

export const useGeneratorStore = create<GeneratorState>((set) => ({
  ...initialState,

  setPrompt: (prompt) =>
    set({
      prompt,
      // 직접 편집 시 파라미터 모드 해제
      templateRawPrompt: null,
      templateParameters: null,
    }),

  setTemplateParameter: (key, value) =>
    set((state) => {
      if (!state.templateRawPrompt || !state.templateParameters) {
        return state
      }
      const newParams = { ...state.templateParameters, [key]: value }
      const newPrompt = resolveTemplate(state.templateRawPrompt, newParams)
      return {
        templateParameters: newParams,
        prompt: newPrompt,
      }
    }),
  setStyle: (style) => set({ style }),
  setModel: (model) => set({ model }),
  setAspectRatio: (aspectRatio) => set({ aspectRatio }),
  setCount: (count) => set({ count }),
  setReferenceImages: (referenceImages) => set({ referenceImages }),
  addReferenceImage: (image) =>
    set((state) => ({
      referenceImages: [...state.referenceImages, image],
    })),
  removeReferenceImagesByGenerationId: (generationId) =>
    set((state) => {
      const generationPrefix = `generations/${generationId}/`
      return {
        referenceImages: state.referenceImages.filter(
          (image) =>
            !image.s3Key.startsWith(generationPrefix) &&
            !image.thumbnailS3Key.startsWith(generationPrefix)
        ),
      }
    }),
  setIsGenerating: (isGenerating) => set({ isGenerating }),
  setResults: (results) => set({ results }),
  setLastPrompt: (lastPrompt) => set({ lastPrompt }),
  setSelectedImage: (selectedImage) => set({ selectedImage }),

  toggleLike: (imageId) =>
    set((state) => ({
      results: state.results.map((img) =>
        img.id === imageId ? { ...img, liked: !img.liked } : img
      ),
    })),

  reset: () => set(initialState),

  setSelectedTemplate: (template) => set({ selectedTemplate: template }),

  clearTemplate: () =>
    set({
      selectedTemplate: null,
      // 스타일 모드로 복귀 시 기본 스타일로 리셋
      style: DEFAULT_STYLE_ID,
      // 파라미터 모드도 해제
      templateRawPrompt: null,
      templateParameters: null,
    }),

  applySettings: (settings) =>
    set((state) => {
      // 템플릿 파라미터 파싱
      const params = parseTemplateParameters(settings.prompt)
      const hasParams = params.length > 0

      // 파라미터가 있으면 전달된 값(없으면 기본값)으로 프롬프트 치환
      const parameterValues = hasParams
        ? { ...extractDefaultValues(params), ...settings.templateParameters }
        : null
      const resolvedPrompt = parameterValues
        ? resolveTemplate(settings.prompt, parameterValues)
        : settings.prompt

      return {
        prompt: resolvedPrompt,
        // undefined는 "미지정", null은 "스타일 없음" - 명시적 구분
        style: settings.style !== undefined ? settings.style : state.style,
        aspectRatio: settings.aspectRatio ?? state.aspectRatio,
        referenceImages: settings.referenceImages ?? [],
        // 템플릿 정보 설정
        selectedTemplate: settings.template ?? null,
        // 템플릿 파라미터 상태 설정
        templateRawPrompt: hasParams ? settings.prompt : null,
        templateParameters: parameterValues,
      }
    }),

  // Queue actions
  enqueueGeneration: (params) => {
    const state = useGeneratorStore.getState()
    // 큐 한도 체크 (pending + processing만 카운트)
    const activeCount = state.queue.filter(
      (item) => item.status === 'pending' || item.status === 'processing'
    ).length
    if (activeCount >= QUEUE_MAX_SIZE) {
      return null // 큐 가득 참
    }

    const queueId = generateUUID()
    const newItem: QueueItem = {
      id: queueId,
      params,
      status: 'pending',
      progress: 0,
      progressKey: 'queue.waiting',
      createdAt: Date.now(),
    }

    set((state) => ({
      queue: [...state.queue, newItem],
      hasGeneratedOnce: true,
      // isGenerating은 큐 프로세서에서 관리하므로 여기서는 건드리지 않음
    }))

    return queueId
  },

  cancelQueueItem: (queueId) =>
    set((state) => ({
      queue: state.queue.filter(
        (item) => !(item.id === queueId && item.status === 'pending')
      ),
    })),

  retryQueueItem: (queueId) =>
    set((state) => ({
      queue: state.queue.map((item) =>
        item.id === queueId && item.status === 'failed'
          ? {
              ...item,
              status: 'pending' as const,
              progress: 0,
              progressKey: 'queue.waiting',
              error: undefined,
            }
          : item
      ),
    })),

  clearCompletedItems: () =>
    set((state) => ({
      queue: state.queue.filter(
        (item) => item.status !== 'completed' && item.status !== 'failed'
      ),
    })),

  _updateQueueItem: (queueId, updates) =>
    set((state) => ({
      queue: state.queue.map((item) =>
        item.id === queueId ? { ...item, ...updates } : item
      ),
      // isGenerating 상태 동기화 (processing 중인 아이템이 있으면 true)
      isGenerating: state.queue.some(
        (item) =>
          item.id === queueId
            ? updates.status === 'processing'
            : item.status === 'processing'
      ),
    })),

  setUsageLimitModalOpen: (open) => set({ usageLimitModalOpen: open }),
  setSsoTokenExpiredModalOpen: (open) => set({ ssoTokenExpiredModalOpen: open }),
  setSafetyErrorModalOpen: (open) => set({ safetyErrorModalOpen: open }),
  setSafetyFlaggedCategories: (categories) => set({ safetyFlaggedCategories: categories }),
  dismissGeneratedOnceHint: () => set({ hasGeneratedOnce: false }),
  requestPromptFocus: () => set((state) => ({ promptFocusTrigger: state.promptFocusTrigger + 1 })),
}))
